import React, { useEffect, useState } from "react";
import { FooterComponents } from "../FooterComponent";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { GraduationCap, MapPin, Calendar, Search } from "lucide-react";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";
import { useNavigate, useParams } from "react-router-dom";

export default function EducationProgramPerScholarship() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the scholarship ID from URL params
  const {type} = useParams(); // Get the scholarship type from URL params
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [programsPerPage] = useState(5); // Set items per page


  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_BASE_URL +
            `hec/education-program/by-program-by-scholarship?scholarship=${id}`,
          { headers: getAuthHeaders() }
        );
        setPrograms(response.data);
      } catch (err) {
        setError("Failed to fetch education programs: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, [id]); // Add id as a dependency

  const filteredPrograms = programs.filter(
    (program) =>
      program.programName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.universityName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate programs for the current page
  const indexOfLastProgram = currentPage * programsPerPage;
  const indexOfFirstProgram = indexOfLastProgram - programsPerPage;
  const currentPrograms = filteredPrograms.slice(
    indexOfFirstProgram,
    indexOfLastProgram
  );

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredPrograms.length / programsPerPage);

  const renderPaginationButtons = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`mx-1 px-3 py-1 rounded-lg text-sm font-medium ${
            currentPage === i
              ? "bg-hec-700 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }
    return pageNumbers;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };


  const applyTheProgram = (program) => {
    sessionStorage.setItem("selectedEducationProgram", JSON.stringify(program));
    sessionStorage.setItem("selectedScholarshipType", type);
    sessionStorage.setItem("selectedScholarshipId", id);
    navigate(`/apply_for_program/${program.id}?type=${type}`);
  };
 
  return (
    <div className="bg-gray-50 min-h-screen">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="max-w-7xl mx-auto py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-hec-700 mb-2">
                Education Programs
              </h1>
              <p className="text-hec-600">
                List of available education programs for the selected
                scholarship.
              </p>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-hec-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search programs by name or university..."
                  className="w-full pl-10 pr-4 py-3 border border-hec-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-transparent transition-all duration-200 text-hec-900"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Loading, Error, or Table */}
            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                  <GraduationCap className="w-8 h-8 text-hec-400" />
                </div>
                <p className="text-hec-600">Loading programs...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
                <p className="font-medium">Error: {error}</p>
                <p>Please try again later.</p>
              </div>
            ) : (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-hec-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-hec-850 border-b border-hec-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Program Name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          University
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Study Years
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Start Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-hec-100">
                      {currentPrograms.map((program) => (
                        <tr
                          key={program.id}
                          className="hover:bg-hec-50 transition-colors duration-150"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-hec-100 rounded-lg flex items-center justify-center">
                                  <GraduationCap className="w-5 h-5 text-hec-800" />
                                </div>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-hec-900 leading-tight">
                                  {program.programName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-4 h-4 text-hec-400" />
                              <div className="text-sm text-hec-900">
                                {program.universityName}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-hec-400" />
                              <div className="text-sm text-hec-900">
                                {program.studyYears} years
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-hec-900">
                              {formatDate(program.startDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-hec-900">
                              {formatDate(program.endDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button className="bg-hec-700 hover:bg-hec-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-hec-500 focus:ring-offset-2"
                            onClick={() => applyTheProgram(program)}
                            >
                              Apply
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {currentPrograms.length === 0 && !loading && !error && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="w-8 h-8 text-hec-400" />
                      </div>
                      <h3 className="text-lg font-medium text-hec-900 mb-2">
                        No programs found
                      </h3>
                      <p className="text-hec-500">
                        Try adjusting your search terms or clear the search to
                        see all programs.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pagination Controls */}
            {!loading &&
              !error &&
              filteredPrograms.length > programsPerPage && (
                <div className="flex justify-center mt-8 space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {renderPaginationButtons()}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
          </div>
        </div>
        <div>
          <FooterComponents />
        </div>
      </div>
    </div>
  );
}
